/**
 * dbt Project sub-section for a warehouse card.
 * Allows linking a GitHub repo + project path to a warehouse.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, CircleCheckBig, ExternalLink, FolderGit2, LoaderCircle, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

interface DbtProjectSectionProps {
  warehouseId: string;
  isAdmin: boolean;
}

export function DbtProjectSection({ warehouseId, isAdmin }: DbtProjectSectionProps): React.ReactElement {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const orpc = useOrpc();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedInstallationId, setSelectedInstallationId] = useState(0);
  const [projectPath, setProjectPath] = useState('');
  const [detectedPaths, setDetectedPaths] = useState<string[] | null>(null);

  const { data, isLoading } = useQuery(orpc.settings.dbt.list.queryOptions({ input: {} }));

  const repos = data?.repos ?? [];
  const mappings = data?.mappings ?? [];
  const currentMapping = mappings.find((m) => m.warehouseId === warehouseId);

  const saveMutation = useMutation(
    orpc.settings.dbt.updateMappings.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.settings.dbt.list.queryOptions({ input: {} }).queryKey,
        });
        toast.success('dbt project saved');
        setIsEditing(false);
        setDetectedPaths(null);
      },
      onError: () => {
        toast.error('Failed to save dbt project');
      },
    }),
  );

  const detectMutation = useMutation(
    orpc.settings.dbt.detectProject.mutationOptions({
      onSuccess: (result, variables) => {
        // Guard against stale responses: only apply if the repo still matches
        if (variables.repoFullName !== selectedRepo) return;
        setDetectedPaths(result.paths);
        if (result.paths.length === 1) {
          setProjectPath(result.paths[0]!);
        }
      },
      onError: (_err, variables) => {
        // Guard against stale error responses (same as success path)
        if (variables.repoFullName !== selectedRepo) return;
        setDetectedPaths([]);
      },
    }),
  );
  const detectMutate = detectMutation.mutate;

  // Pre-fill form when editing existing mapping
  useEffect(() => {
    if (isEditing && currentMapping) {
      setSelectedRepo(currentMapping.repoFullName);
      setSelectedInstallationId(currentMapping.installationId);
      setProjectPath(currentMapping.projectPath);
    }
  }, [isEditing, currentMapping]);

  // Auto-detect paths when repo is selected
  useEffect(() => {
    if (selectedRepo && selectedInstallationId >= 0 && isEditing) {
      setDetectedPaths(null);
      detectMutate({
        installationId: selectedInstallationId,
        repoFullName: selectedRepo,
      });
    }
  }, [selectedRepo, selectedInstallationId, isEditing, detectMutate]);

  function handleSave(): void {
    // Build the full mappings array: keep other warehouse mappings, update this one
    const otherMappings = mappings
      .filter((m) => m.warehouseId !== warehouseId)
      .map((m) => ({
        warehouseId: m.warehouseId,
        installationId: m.installationId,
        repoFullName: m.repoFullName,
        projectPath: m.projectPath,
      }));

    saveMutation.mutate({
      mappings: [
        ...otherMappings,
        {
          warehouseId,
          installationId: selectedInstallationId,
          repoFullName: selectedRepo,
          projectPath,
        },
      ],
    });
  }

  function handleRemove(): void {
    const otherMappings = mappings
      .filter((m) => m.warehouseId !== warehouseId)
      .map((m) => ({
        warehouseId: m.warehouseId,
        installationId: m.installationId,
        repoFullName: m.repoFullName,
        projectPath: m.projectPath,
      }));

    saveMutation.mutate({ mappings: otherMappings });
  }

  function handleRepoChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const value = e.target.value;
    setSelectedRepo(value);
    setProjectPath('');
    setDetectedPaths(null);

    const repo = repos.find((r) => r.fullName === value);
    setSelectedInstallationId(repo?.installationId ?? 0);
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <LoaderCircle size={12} className="animate-spin" />
        Loading dbt settings...
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-border/50 pt-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderGit2 size={13} className="text-muted-foreground" />
          <span className="text-xs font-medium tracking-wider text-muted-foreground uppercase">dbt Project</span>
        </div>
        {isAdmin && (
          <a
            href={`https://github.com/apps/${workspace.githubAppSlug ?? 'rudder-ai-app'}/installations/select_target?state=${workspace.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[0.6875rem] text-muted-foreground transition-colors hover:text-brand"
          >
            <ExternalLink size={10} />
            Configure GitHub
          </a>
        )}
      </div>

      {/* Connected state — show compact summary */}
      {currentMapping && !isEditing && (
        <div className="flex items-center gap-2">
          <CircleCheckBig size={13} className="shrink-0 text-green-600" />
          <span className="min-w-0 flex-1 truncate text-xs text-foreground">
            {currentMapping.repoFullName}
            {currentMapping.projectPath !== '/' && (
              <span className="text-muted-foreground"> ({currentMapping.projectPath})</span>
            )}
          </span>
          {isAdmin && (
            <div className="flex shrink-0 items-center gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground transition-colors hover:text-brand"
              >
                Edit
              </button>
              <button
                onClick={handleRemove}
                disabled={saveMutation.isPending}
                className="rounded px-2 py-0.5 text-[0.6875rem] text-muted-foreground transition-colors hover:text-red-700"
              >
                <Trash2 size={11} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Not connected — show connect button (only when repos available) */}
      {!currentMapping && !isEditing && isAdmin && repos.length > 0 && (
        <button
          onClick={() => setIsEditing(true)}
          className="inline-flex items-center gap-1.5 rounded border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
        >
          <ChevronDown size={11} />
          Connect dbt project
        </button>
      )}

      {/* No repos — nudge to configure GitHub */}
      {!currentMapping && !isEditing && repos.length === 0 && (
        <p className="text-[0.6875rem] text-muted-foreground">
          No GitHub repositories available. Use the link above to configure access.
        </p>
      )}

      {/* Editing form */}
      {isEditing && (
        <div className="mt-2 space-y-2">
          {/* Repo dropdown */}
          <div>
            <label className="mb-1 block text-[0.6875rem] text-muted-foreground">Repository</label>
            <select
              value={selectedRepo}
              onChange={handleRepoChange}
              className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-brand"
            >
              <option value="">Select a repository...</option>
              {repos.map((r) => (
                <option key={r.fullName} value={r.fullName}>
                  {r.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* Project path */}
          {selectedRepo && (
            <div>
              <label className="mb-1 block text-[0.6875rem] text-muted-foreground">Project Path</label>
              {detectMutation.isPending && (
                <div className="flex items-center gap-1.5 py-1 text-[0.6875rem] text-muted-foreground">
                  <LoaderCircle size={11} className="animate-spin" />
                  Detecting dbt_project.yml...
                </div>
              )}
              {detectedPaths && detectedPaths.length > 0 && (
                <div className="mb-1.5 space-y-1">
                  {detectedPaths.map((p) => (
                    <label
                      key={p}
                      className="flex cursor-pointer items-center gap-2 rounded border border-border px-2.5 py-1.5 text-xs transition-colors hover:border-brand"
                    >
                      <input
                        type="radio"
                        name={`dbt-path-${warehouseId}`}
                        value={p}
                        checked={projectPath === p}
                        onChange={() => setProjectPath(p)}
                        className="accent-brand"
                      />
                      <code className="font-mono text-[0.6875rem]">{p}</code>
                    </label>
                  ))}
                </div>
              )}
              {detectedPaths?.length === 0 && (
                <p className="mb-1.5 text-[0.6875rem] text-amber-600">
                  No dbt_project.yml found. Enter the path manually.
                </p>
              )}
              <input
                type="text"
                value={projectPath}
                onChange={(e) => setProjectPath(e.target.value)}
                placeholder="/"
                className="w-full rounded border border-border bg-background px-2.5 py-1.5 font-mono text-xs text-foreground outline-none placeholder:text-muted-foreground focus:border-brand"
              />
            </div>
          )}

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={!selectedRepo || !projectPath || saveMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded border border-brand bg-brand/10 px-2.5 py-1 text-xs text-brand transition-colors hover:bg-brand/20 disabled:opacity-30"
            >
              {saveMutation.isPending && <LoaderCircle size={11} className="animate-spin" />}
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setDetectedPaths(null);
              }}
              className="rounded px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
